import React, { useEffect, useState, ReactNode } from 'react';
import { Breadcrumb, message, Tooltip } from 'antd';
import Separator from 'appJS/assets/images/separator.svg';
import { Organization, Project, User } from './types';
import styles from './index.module.css';
import { Star } from 'lucide-react';
import { put } from 'utils/fetch';
import cx from 'classnames';
interface Props {
  organization: Organization;
  organizations: Organization[];
  isMobileView: boolean;
  disabledProjects: Project[];
  user: User;
  setUser: (user: User) => void;
  organizationId?: number;
  projectFoldersAllowed?: boolean;
}

export const BreadCrumbs: React.FC<Props> = ({
  organization,
  organizations,
  isMobileView,
  disabledProjects,
  user,
  setUser,
  organizationId,
  projectFoldersAllowed
}) => {
  const [pathname, setPathname] = useState<string>(window.location.pathname);
  useEffect(() => {
    // @ts-expect-error
    window.setBreadcrumbsPathname = setPathname;
  }, []);
  let defaultTitle = document?.title?.replace(' | BugHerd', '');

  type Item = {
    onClick?: (e: any) => void;
    title: ReactNode;
    className: string;
  };

  const titleItem = ({ title = defaultTitle }: { title?: string }): Item => ({
    title: <span title={title}>{title}</span>,
    className: styles.pageTitle
  });

  const items: Item[] = [];

  const splitPathname = pathname.split('/').filter(_pathname => _pathname);
  const totalParams = splitPathname.length;
  const lastIndex = totalParams - 1;
  const firstParam = splitPathname[0];
  const lastParam = splitPathname[lastIndex];

  const parsedOrganizationId = Number(
    pathname?.match(/\/organizations\/(\d+)/)?.[1]
  );
  const parsedProjectId = Number(pathname?.match(/^\/projects\/(\d+)/)?.[1]);

  const isOrganizationSettings = /\/settings\/organizations\/\d+\/\w+/.test(
    pathname
  );
  const isOrganizationIntegrationsSettings = /\/organizations\/\d+\/integrations\/\d+\/\w+/.test(
    pathname
  );
  const isIntegrationsProject = /\/integrations\/\d+\/projects/.test(pathname);

  const hasOrganizationName = parsedOrganizationId || parsedProjectId;

  const currentOrganization = organizations.find(
    ({ id }) => id === parsedOrganizationId
  );

  const isDisabledProject = !!disabledProjects?.find(
    ({ id }) => id === parsedProjectId
  );

  if (isDisabledProject) {
    defaultTitle += ' (Disabled)';
  }

  const organizationNameItem = (): Item => {
    const title = (currentOrganization || organization)?.display_name;
    return {
      title: (
        <span id="org-name-title" title={title}>
          {title}
        </span>
      ),
      className: styles.organizationName
    };
  };

  const titles: { [key: string]: string } = {
    settings: 'Settings',
    projects: 'My Projects',
    dashboard: 'Dashboard',
    notifications: 'Notifications',
    profile: 'Profile'
  };

  const lastTitle = titleItem({ title: titles[lastParam] });
  const settingsItem = titleItem({ title: titles.settings });
  const orgNameItem = organizationNameItem();
  const defaultTitleItem = titleItem({});

  if (hasOrganizationName) {
    items.push(orgNameItem);
    if (parsedProjectId) {
      items.push(defaultTitleItem);
    } else if (
      isOrganizationSettings ||
      isOrganizationIntegrationsSettings ||
      isIntegrationsProject
    ) {
      items.push(settingsItem);
    } else {
      items.push(lastTitle);
    }
  } else if (firstParam === 'settings') {
    items.push(settingsItem);
    items.push(lastTitle);
  }

  //Todo display breadcrumbs one more level deep after project settings page is ready

  const mobileItems = [
    {
      title: <span>{defaultTitle}</span>,
      className: styles.mobileTitle
    }
  ];

  let breadcrumbItems: Item[];
  if (isMobileView) {
    breadcrumbItems = mobileItems;
  } else if (items.length) {
    breadcrumbItems = items;
  } else {
    breadcrumbItems = [defaultTitleItem];
  }

  const mobileProjectTitle =
    isMobileView && breadcrumbItems.includes(defaultTitleItem);

  let favourites: { [key: string]: string[] } = {};

  try {
    if (user.favourite_projects) {
      favourites = JSON.parse(user.favourite_projects);
    }
  } catch (error) {
    console.error('Error parsing favourite projects', error);
  }

  const organizationFavourites = !organizationId
    ? []
    : favourites[organizationId] || [];
  const favouriteProjectParams =
    parsedProjectId && organizationId && user.favourite_projects;
  const isProjectFavourited = !!(
    favouriteProjectParams &&
    organizationFavourites.includes(String(parsedProjectId))
  );

  const onFavouriteClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!favouriteProjectParams || !user.id) return;

    const projectIdString = String(parsedProjectId);

    const removing = organizationFavourites?.includes(projectIdString);
    const newFavourites = removing
      ? organizationFavourites.filter(
          favourite => favourite !== projectIdString
        )
      : [...organizationFavourites, projectIdString];

    try {
      const favourite_projects = JSON.stringify(
        Object.assign(favourites, { [organizationId]: newFavourites })
      );
      const response = await put(`/users/${user.id}`, {
        favourite_projects,
        removing,
        organization_id: organizationId,
        project_id: parsedProjectId
      });

      if (response.user) {
        message.success(
          `Project was ${removing ? 'removed from' : 'added to'} favorites`
        );
        setUser({ ...user, favourite_projects });
      }
    } catch (error) {
      console.error('Error updating favorite projects', error);
      message.error('Failed to update favorite status');
    }
  };

  const showStar =
    projectFoldersAllowed &&
    (breadcrumbItems.includes(defaultTitleItem) || mobileProjectTitle) &&
    parsedProjectId &&
    organizationId &&
    user.id;

  return (
    <>
      <Breadcrumb
        separator={<Separator />}
        items={breadcrumbItems}
        className={styles.breadcrumbsContainer}
      />
      {showStar && (
        <Tooltip
          title={
            isProjectFavourited ? 'Remove from favorites' : 'Add to favorites'
          }
        >
          <Star
            className={cx(styles.favouriteProject, {
              [styles.favourited]: isProjectFavourited
            })}
            onClick={onFavouriteClick}
            fill={isProjectFavourited ? 'currentColor' : 'none'}
          />
        </Tooltip>
      )}
    </>
  );
};
